/**
 * Live predictive search results inside the header search overlay
 * (assets/header.js's <search-overlay> owns opening/closing/focus/trap/
 * scroll-lock; this element only owns fetching and rendering results as the
 * user types). Debounces input, aborts any in-flight request when a newer
 * keystroke lands, and swaps the server-rendered default columns
 * (data-predictive-default - popular searches, brand pills, most-searched
 * products, explore collections, all rendered in sections/header.liquid)
 * for live results (data-predictive-results) while a query is present;
 * clearing the input restores the default state.
 *
 * Results are built with DOM calls rather than Shopify's predictive-search
 * "sections" rendering API: the plain /search/suggest.json endpoint already
 * returns everything the compact row needs (title, url, image, vendor,
 * formatted price) without an extra section-render round trip, and
 * innerHTML string-building from that JSON would mean re-escaping
 * merchant/customer-influenced text by hand - createElement + textContent
 * gets that for free.
 */
if (!customElements.get('predictive-search')) {
  const DEBOUNCE_MS = 200;
  const RESOURCE_LIMIT = 6;

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('[data-predictive-search-input]');
      this.defaultRegion = this.querySelector('[data-predictive-default]');
      this.resultsRegion = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.defaultRegion || !this.resultsRegion) return;

      this.emptyTemplate = this.dataset.emptyText || '';
      this.controller = null;
      this.debounceTimer = null;

      this.onInput = this.onInput.bind(this);
      this.input.addEventListener('input', this.onInput);

      // A query typed last time the overlay was open shouldn't leave stale
      // results showing behind a freshly cleared input the next time it
      // opens. <details>' toggle event doesn't bubble, so this listens on
      // the ancestor directly rather than relying on delegation from above.
      this.details = this.closest('details');
      this.onToggle = this.onToggle.bind(this);
      if (this.details) this.details.addEventListener('toggle', this.onToggle);

      // Clicking a popular-search pill (data-popular-term, rendered in the
      // default state) should fill the input and run the search in place
      // rather than navigating away - it's a real link to /search?q=... so
      // it still works with no JS.
      this.onDefaultClick = this.onDefaultClick.bind(this);
      this.defaultRegion.addEventListener('click', this.onDefaultClick);
    }

    disconnectedCallback() {
      this.input?.removeEventListener('input', this.onInput);
      this.defaultRegion?.removeEventListener('click', this.onDefaultClick);
      if (this.details) this.details.removeEventListener('toggle', this.onToggle);
      clearTimeout(this.debounceTimer);
      if (this.controller) this.controller.abort();
    }

    onToggle() {
      if (this.details.open) return;
      clearTimeout(this.debounceTimer);
      if (this.controller) this.controller.abort();
      this.showDefault();
    }

    onDefaultClick(event) {
      const pill = event.target.closest('[data-popular-term]');
      if (!pill) return;
      event.preventDefault();
      this.input.value = pill.dataset.popularTerm;
      this.input.focus();
      this.input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    onInput() {
      const query = this.input.value.trim();
      clearTimeout(this.debounceTimer);

      if (query === '') {
        if (this.controller) this.controller.abort();
        this.showDefault();
        return;
      }

      this.debounceTimer = setTimeout(() => this.search(query), DEBOUNCE_MS);
    }

    showDefault() {
      this.resultsRegion.hidden = true;
      this.resultsRegion.textContent = '';
      this.defaultRegion.hidden = false;
    }

    async search(query) {
      if (this.controller) this.controller.abort();
      const controller = new AbortController();
      this.controller = controller;

      /* unavailable_products=show: rank sold-out products by relevance, not
         last. A drop store is mostly sold out between restocks; with "last",
         searching "x2 crazylight limited" put skates and a dongle above the
         X2 CrazyLight Limited & Special Editions it was asking for. */
      const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,query&resources[limit]=${RESOURCE_LIMIT}&resources[options][unavailable_products]=show`;

      let data;
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error(`Predictive search request failed: ${response.status}`);
        data = await response.json();
      } catch (error) {
        if (error.name === 'AbortError') return;
        this.showDefault();
        return;
      }

      // A newer keystroke may have started (and resolved) a later request
      // while this one was in flight; only the most recent controller is
      // still current. An aborted fetch normally throws and short-circuits
      // above, but this guards the rare case where it resolved in the same
      // tick it was superseded.
      if (this.controller !== controller) return;

      this.render(data.resources.results, query);
    }

    render(results, query) {
      const products = results.products || [];
      const collections = results.collections || [];

      this.defaultRegion.hidden = true;
      this.resultsRegion.hidden = false;
      this.resultsRegion.textContent = '';

      if (products.length === 0 && collections.length === 0) {
        const empty = document.createElement('p');
        empty.className = 'search-overlay__empty';
        empty.textContent = this.emptyTemplate.replace('__TERMS__', query);
        this.resultsRegion.appendChild(empty);
        return;
      }

      const columns = document.createElement('div');
      columns.className = 'search-overlay__columns';

      if (products.length > 0) columns.appendChild(this.buildProductColumn(products));
      if (collections.length > 0) columns.appendChild(this.buildCollectionColumn(collections));

      this.resultsRegion.appendChild(columns);
    }

    buildColumn(labelKey) {
      const column = document.createElement('div');
      column.className = 'search-overlay__column';
      const heading = document.createElement('h2');
      heading.className = 'search-overlay__heading h6';
      heading.textContent = this.dataset[labelKey] || '';
      column.appendChild(heading);
      return column;
    }

    buildProductColumn(products) {
      const column = this.buildColumn('productsLabel');
      const rows = document.createElement('div');
      rows.className = 'search-overlay__rows';

      products.forEach((product) => {
        const row = document.createElement('a');
        row.className = 'search-overlay__row';
        row.href = product.url;

        const media = document.createElement('span');
        media.className = 'search-overlay__row-media';
        if (product.image) {
          const img = document.createElement('img');
          img.src = product.image;
          img.width = 60;
          img.height = 60;
          img.loading = 'lazy';
          img.alt = '';
          media.appendChild(img);
        }
        row.appendChild(media);

        const info = document.createElement('span');
        info.className = 'search-overlay__row-info';

        if (product.vendor) {
          const vendor = document.createElement('span');
          vendor.className = 'search-overlay__row-vendor';
          vendor.textContent = product.vendor;
          info.appendChild(vendor);
        }

        const title = document.createElement('span');
        title.className = 'search-overlay__row-title';
        title.textContent = product.title;
        info.appendChild(title);

        if (product.price) {
          const price = document.createElement('span');
          price.className = 'search-overlay__row-price';
          price.textContent = product.price;
          info.appendChild(price);
        }

        row.appendChild(info);
        rows.appendChild(row);
      });

      column.appendChild(rows);
      return column;
    }

    buildCollectionColumn(collections) {
      const column = this.buildColumn('collectionsLabel');
      const rows = document.createElement('div');
      rows.className = 'search-overlay__rows';

      collections.forEach((collection) => {
        const row = document.createElement('a');
        row.className = 'search-overlay__collection-row';
        row.href = collection.url;

        const media = document.createElement('span');
        media.className = 'search-overlay__row-media';
        if (collection.image) {
          const img = document.createElement('img');
          img.src = collection.image;
          img.width = 60;
          img.height = 60;
          img.loading = 'lazy';
          img.alt = '';
          media.appendChild(img);
        }
        row.appendChild(media);

        const title = document.createElement('span');
        title.className = 'search-overlay__row-title';
        title.textContent = collection.title;
        row.appendChild(title);

        rows.appendChild(row);
      });

      column.appendChild(rows);
      return column;
    }
  }

  customElements.define('predictive-search', PredictiveSearch);
}
